/**
 * Daily Profit/Loss Circuit Breaker Card.
 *
 * Renders the master toggles and percentage limits for the daily circuit
 * breaker, plus a live status snapshot. Shown inside the Trading Master
 * Switches tab (under Global Trading).
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import {
  getSettingsSchema,
  updateSettings,
  type SettingDefinition,
  type SettingsSchema,
} from "@app/services/adminSettingsApi";
import {
  getDailyCircuitBreakerStatus,
} from "@app/services/adminSystemSettingsApi";
import { logger } from "@app/services/logger";

const PROFIT_ENABLED_KEY = "circuit_breaker.daily_profit_limit_enabled";
const PROFIT_PERCENT_KEY = "circuit_breaker.daily_profit_limit_percent";
const LOSS_ENABLED_KEY = "circuit_breaker.daily_loss_limit_enabled";
const LOSS_PERCENT_KEY = "circuit_breaker.daily_loss_limit_percent";
const TRAILING_ENABLED_KEY = "circuit_breaker.daily_profit_trailing_enabled";
const TRAILING_ARM_KEY = "circuit_breaker.daily_profit_trailing_arm_percent";
const TRAILING_DRAWDOWN_KEY = "circuit_breaker.daily_profit_trailing_drawdown_percent";

interface CircuitBreakerConfig {
  profitEnabled: boolean;
  profitPercent: number;
  lossEnabled: boolean;
  lossPercent: number;
  trailingEnabled: boolean;
  trailingArm: number;
  trailingDrawdown: number;
}

function extractConfig(schema: SettingsSchema | undefined): CircuitBreakerConfig {
  const defaults: CircuitBreakerConfig = {
    profitEnabled: false,
    profitPercent: 3.0,
    lossEnabled: false,
    lossPercent: 3.0,
    trailingEnabled: false,
    trailingArm: 3.0,
    trailingDrawdown: 1.0,
  };

  if (!schema || !schema.CircuitBreaker) {
    return defaults;
  }

  const settings: SettingDefinition[] = Object.values(
    schema.CircuitBreaker,
  ).flat();

  const byKey = new Map(settings.map((s) => [s.key, s]));
  const readBool = (key: string, fallback: boolean): boolean =>
    byKey.get(key)?.current_value ?? fallback;
  const readNum = (key: string, fallback: number): number =>
    Number(byKey.get(key)?.current_value ?? fallback);

  return {
    profitEnabled: readBool(PROFIT_ENABLED_KEY, defaults.profitEnabled),
    profitPercent: readNum(PROFIT_PERCENT_KEY, defaults.profitPercent),
    lossEnabled: readBool(LOSS_ENABLED_KEY, defaults.lossEnabled),
    lossPercent: readNum(LOSS_PERCENT_KEY, defaults.lossPercent),
    trailingEnabled: readBool(TRAILING_ENABLED_KEY, defaults.trailingEnabled),
    trailingArm: readNum(TRAILING_ARM_KEY, defaults.trailingArm),
    trailingDrawdown: readNum(TRAILING_DRAWDOWN_KEY, defaults.trailingDrawdown),
  };
}

export function DailyCircuitBreakerCard(): JSX.Element {
  const queryClient = useQueryClient();
  const [profitDraft, setProfitDraft] = useState<string>("");
  const [lossDraft, setLossDraft] = useState<string>("");
  const [trailingArmDraft, setTrailingArmDraft] = useState<string>("");
  const [trailingDrawdownDraft, setTrailingDrawdownDraft] = useState<string>("");

  const { data: schema, isLoading: schemaLoading } = useQuery<SettingsSchema>({
    queryKey: ["adminSettingsSchema", "CircuitBreaker"],
    queryFn: () => getSettingsSchema("CircuitBreaker"),
  });

  const { data: status } = useQuery({
    queryKey: ["dailyCircuitBreakerStatus"],
    queryFn: getDailyCircuitBreakerStatus,
    refetchInterval: 300000, // refresh every 5 minutes
  });

  const config = useMemo(() => extractConfig(schema), [schema]);

  // Sync the draft inputs with the actual values loaded from the database.
  // Without this, the inputs stay frozen at their initial placeholder ("")
  // and never reflect the stored value (e.g. a previously saved 6.0 would
  // still render as the 3.0 default), making the admin believe the upper
  // limit is lower than it really is and that saves "do not stick".
  useEffect(() => {
    if (config.profitPercent != null) {
      setProfitDraft(String(config.profitPercent));
    }
    if (config.lossPercent != null) {
      setLossDraft(String(config.lossPercent));
    }
    if (config.trailingArm != null) {
      setTrailingArmDraft(String(config.trailingArm));
    }
    if (config.trailingDrawdown != null) {
      setTrailingDrawdownDraft(String(config.trailingDrawdown));
    }
  }, [config.profitPercent, config.lossPercent, config.trailingArm, config.trailingDrawdown]);

  const toggleMutation = useMutation({
    mutationFn: (payload: { key: string; value: boolean }) =>
      updateSettings({ updates: [payload] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminSettingsSchema"] });
    },
    onError: (error: any) => {
      logger.error("Failed to toggle circuit breaker", error);
      alert(
        `Failed to update circuit breaker: ${error.response?.data?.detail || error.message}`,
      );
    },
  });

  const percentMutation = useMutation({
    mutationFn: (payload: { key: string; value: number }) =>
      updateSettings({ updates: [payload] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminSettingsSchema"] });
    },
    onError: (error: any) => {
      logger.error("Failed to update circuit breaker percent", error);
      alert(
        `Failed to update circuit breaker percent: ${error.response?.data?.detail || error.message}`,
      );
    },
  });

  const handleSavePercent = (key: string, raw: string) => {
    const value = Number(raw);
    if (Number.isNaN(value) || value < 0.1 || value > 100) {
      alert("Please enter a value between 0.1 and 100");
      return;
    }
    percentMutation.mutate({ key, value });
  };

  const statusText = status?.halted
    ? `Halted (${status.reason ?? "unknown"})`
    : "Active";
  const pnlText =
    status?.pnl_pct != null ? `${status.pnl_pct.toFixed(2)}% today` : "—";

  const trailing = status?.trailing ?? null;
  const trailingStatusText = trailing?.enabled
    ? trailing.armed
      ? `Armed — peak ${trailing.peak_pnl_pct?.toFixed(2)}%, halts at ${trailing.halt_at_pnl_pct?.toFixed(2)}%`
      : `Arms at ${trailing.arm_pct?.toFixed(2)}%`
    : "Disabled";

  if (schemaLoading) {
    return (
      <Card className="admin-trading-pipeline-card">
        <div className="admin-trading-pipeline-switch-item">
          <div className="admin-trading-pipeline-switch-info">
            <h4>Daily Profit/Loss Circuit Breaker</h4>
            <p className="admin-trading-pipeline-switch-description">
              Loading configuration...
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="admin-trading-pipeline-card">
      <div className="admin-trading-pipeline-warning">
        <strong>Daily Profit/Loss Circuit Breaker</strong> — status:{" "}
        <strong>{statusText}</strong> · {pnlText}. When a configured limit is
        reached, new entries halt system-wide while exits keep running. Resets
        at UTC midnight.
      </div>

      <div className="admin-trading-pipeline-switches">
        {/* Profit limit */}
        <div className="admin-trading-pipeline-switch-item">
          <div className="admin-trading-pipeline-switch-info">
            <div className="admin-trading-pipeline-switch-header">
              <h4>Daily Profit Limit</h4>
              <span
                className={`admin-trading-pipeline-status-badge ${
                  config.profitEnabled
                    ? "admin-trading-pipeline-status-enabled"
                    : "admin-trading-pipeline-status-disabled"
                }`}
              >
                {config.profitEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>
            <p className="admin-trading-pipeline-switch-description">
              Halt new entries once today's profit reaches this percentage of
              the admin's total equity.
            </p>
            <div className="admin-trading-pipeline-inline-control">
              <label htmlFor="cb-profit-percent">Limit %</label>
              <input
                id="cb-profit-percent"
                type="number"
                min={0.1}
                max={100}
                step={0.1}
                defaultValue={config.profitPercent}
                value={profitDraft || String(config.profitPercent)}
                onChange={(e) => setProfitDraft(e.target.value)}
              />
              <Button
                type="button"
                variant="primary"
                disabled={percentMutation.isPending}
                onClick={() =>
                  handleSavePercent(PROFIT_PERCENT_KEY, profitDraft || String(config.profitPercent))
                }
              >
                Save
              </Button>
            </div>
          </div>
          <Button
            type="button"
            variant={config.profitEnabled ? "danger" : "primary"}
            disabled={toggleMutation.isPending}
            onClick={() =>
              toggleMutation.mutate({
                key: PROFIT_ENABLED_KEY,
                value: !config.profitEnabled,
              })
            }
          >
            {config.profitEnabled ? "Disable" : "Enable"}
          </Button>
        </div>

        {/* Loss limit */}
        <div className="admin-trading-pipeline-switch-item">
          <div className="admin-trading-pipeline-switch-info">
            <div className="admin-trading-pipeline-switch-header">
              <h4>Daily Loss Limit</h4>
              <span
                className={`admin-trading-pipeline-status-badge ${
                  config.lossEnabled
                    ? "admin-trading-pipeline-status-enabled"
                    : "admin-trading-pipeline-status-disabled"
                }`}
              >
                {config.lossEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>
            <p className="admin-trading-pipeline-switch-description">
              Halt new entries once today's loss reaches this percentage of the
              admin's total equity.
            </p>
            <div className="admin-trading-pipeline-inline-control">
              <label htmlFor="cb-loss-percent">Limit %</label>
              <input
                id="cb-loss-percent"
                type="number"
                min={0.1}
                max={100}
                step={0.1}
                defaultValue={config.lossPercent}
                value={lossDraft || String(config.lossPercent)}
                onChange={(e) => setLossDraft(e.target.value)}
              />
              <Button
                type="button"
                variant="primary"
                disabled={percentMutation.isPending}
                onClick={() =>
                  handleSavePercent(LOSS_PERCENT_KEY, lossDraft || String(config.lossPercent))
                }
              >
                Save
              </Button>
            </div>
          </div>
          <Button
            type="button"
            variant={config.lossEnabled ? "danger" : "primary"}
            disabled={toggleMutation.isPending}
            onClick={() =>
              toggleMutation.mutate({
                key: LOSS_ENABLED_KEY,
                value: !config.lossEnabled,
              })
            }
          >
            {config.lossEnabled ? "Disable" : "Enable"}
          </Button>
        </div>

        {/* Profit trailing stop */}
        <div className="admin-trading-pipeline-switch-item">
          <div className="admin-trading-pipeline-switch-info">
            <div className="admin-trading-pipeline-switch-header">
              <h4>Daily Profit Trailing</h4>
              <span
                className={`admin-trading-pipeline-status-badge ${
                  config.trailingEnabled
                    ? "admin-trading-pipeline-status-enabled"
                    : "admin-trading-pipeline-status-disabled"
                }`}
              >
                {config.trailingEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>
            <p className="admin-trading-pipeline-switch-description">
              Arms once today's profit reaches the arm %, then halts new entries
              if profit retraces from its intraday peak by the drawdown %. Status:{" "}
              <strong>{trailingStatusText}</strong>.
            </p>
            <div className="admin-trading-pipeline-inline-control">
              <label htmlFor="cb-trailing-arm">Arm %</label>
              <input
                id="cb-trailing-arm"
                type="number"
                min={0.1}
                max={100}
                step={0.1}
                defaultValue={config.trailingArm}
                value={trailingArmDraft || String(config.trailingArm)}
                onChange={(e) => setTrailingArmDraft(e.target.value)}
              />
              <label htmlFor="cb-trailing-drawdown">Drawdown %</label>
              <input
                id="cb-trailing-drawdown"
                type="number"
                min={0.1}
                max={100}
                step={0.1}
                defaultValue={config.trailingDrawdown}
                value={trailingDrawdownDraft || String(config.trailingDrawdown)}
                onChange={(e) => setTrailingDrawdownDraft(e.target.value)}
              />
              <Button
                type="button"
                variant="primary"
                disabled={percentMutation.isPending}
                onClick={() =>
                  handleSavePercent(TRAILING_ARM_KEY, trailingArmDraft || String(config.trailingArm))
                }
              >
                Save Arm
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={percentMutation.isPending}
                onClick={() =>
                  handleSavePercent(TRAILING_DRAWDOWN_KEY, trailingDrawdownDraft || String(config.trailingDrawdown))
                }
              >
                Save Drawdown
              </Button>
            </div>
          </div>
          <Button
            type="button"
            variant={config.trailingEnabled ? "danger" : "primary"}
            disabled={toggleMutation.isPending}
            onClick={() =>
              toggleMutation.mutate({
                key: TRAILING_ENABLED_KEY,
                value: !config.trailingEnabled,
              })
            }
          >
            {config.trailingEnabled ? "Disable" : "Enable"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
